import React, {useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';


import {DateRangeInput} from 'src/components/input';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {ConferenceNameInput} from 'src/modules/create-conference/components/conference-name-input';
import {
    CONFERENCE_END_DATE_BEFORE_START_DATE_ERROR,
    CONFERENCE_END_DATE_BEFORE_START_DATE_MESSAGE,
    END_DATE_CONTROL,
} from 'src/modules/create-conference/constants';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {
    useCreateConferenceForAccount,
} from 'src/modules/profile/account/tabs/confenrence/create-conference/useCreateConferenceForAccount';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';

import type {CreateConferenceRequest} from 'src/openapi/admin/models/CreateConferenceRequest';

type Props = {
    onClose: () => void,
    accountId: number,
    onAssign: () => void,
};

export const CreateConferenceModal: React.FC<Props> = props => {
    const {onClose, accountId, onAssign} = props;
    const {addError, removeError, validationErrors} = useContext(ValidationContext);
    const [confStartDate, setConfStartDate] = useState('');
    const [confEndDate, setConfEndDate] = useState('');
    const {isAfterDate} = useDateTime();
    const [serverError, setServerError] = useState('');
    const {hierarchy} = useSelector(selectAccountDetails);
    const [conferenceName, setConferenceName] = useState(hierarchy?.current?.name ?? 'New Conference');
    const {busy, event} = useCreateConferenceForAccount({
        accountId,
        onSuccess: () => {
            onClose();
            onAssign();
        },
        onFail: error => {
            console.error('Error creating conference:', error);
            setServerError('Error creating conference');
        },
    });

    const handleSave = () => {
        if (!validationErrors.length) {
            const createConferenceRequest: CreateConferenceRequest = {
                conferenceName,
                confStartDate,
                confEndDate,
            };
            event(createConferenceRequest);
        }
    };

    useEffect(() => {
        if (confStartDate && confEndDate && isAfterDate(confStartDate, confEndDate) && confStartDate !== confEndDate) {
            addError({
                controlId: END_DATE_CONTROL,
                code: CONFERENCE_END_DATE_BEFORE_START_DATE_ERROR,
                message: CONFERENCE_END_DATE_BEFORE_START_DATE_MESSAGE,
            });
        } else {
            removeError(END_DATE_CONTROL, [CONFERENCE_END_DATE_BEFORE_START_DATE_ERROR]);
        }
    }, [confStartDate, confEndDate, addError, removeError, isAfterDate]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Create Conference
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {serverError ? (
                    <Message color="error" onClose={() => setServerError('')}>
                        {serverError}
                    </Message>
                ) : null}
                <Group direction="v">
                    <WidthItem width={320}>
                        <Label>Conference Name</Label>
                        <ConferenceNameInput
                            conferenceName={conferenceName}
                            onChange={setConferenceName}
                        />
                    </WidthItem>
                    <WidthItem width={320}>
                        <Label>Conference Period</Label>
                        <Group direction="v" gap="s">
                            <DateRangeInput
                                id="conference-start-date-picker"
                                width={320}
                                label="Starts"
                                clearable={true}
                                startDate={confStartDate}
                                endDate={confEndDate}
                                selectRangeType="start"
                                onChange={setConfStartDate}
                            />
                            <ValidatedControl name={END_DATE_CONTROL}>
                                {error => (
                                    <DateRangeInput
                                        id="conference-end-date-picker"
                                        width={320}
                                        label={<>Ends&nbsp;&nbsp;</>}
                                        invalid={error}
                                        clearable={true}
                                        preventPastDate={true}
                                        startDate={confStartDate}
                                        endDate={confEndDate}
                                        selectRangeType="end"
                                        onChange={setConfEndDate}
                                    />
                                )}
                            </ValidatedControl>
                        </Group>
                    </WidthItem>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button
                        variant="Type.PRIMARY"
                        onClick={handleSave}
                        disabled={!conferenceName || !confEndDate || !confStartDate}
                        busy={busy}>
                        Create
                    </Button>
                    <Button
                        variant="Type.SECONDARY"
                        onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
